/**
 * A versao da ficha na tela (robos de uso real, 20/10/2026, achados 1 e 10).
 *
 * O servidor recusa com 409 `ficha_alterada` o formulario aberto antes de outra
 * gravacao (`backend/src/http/versao-da-ficha.ts`), e com `situacao_mudou` a
 * folha de situacao aberta antes de outra mudanca. Aqui ficam as tres pecas
 * que toda tela de ficha usa, puras para terem teste:
 *
 *  - reconhecer a recusa pelo CODIGO (o 409 tem outros donos);
 *  - mandar a versao que a tela guardou AO ABRIR — nunca a da lista relida,
 *    que faria o formulario velho gravar por cima com a versao nova;
 *  - rebasear: ao reler a ficha, o campo que so a outra pessoa mudou fica com
 *    o valor dela, o que voce mudou fica com o seu, e a tela diz quais.
 */

export const CODIGO_FICHA_ALTERADA = 'ficha_alterada';
export const CODIGO_SITUACAO_MUDOU = 'situacao_mudou';

/** O codigo de qualquer camada de pedido (`ErroDaApi`, `RecusaDaApi`). */
export function codigoDaRecusa(erro: unknown): string | null {
  if (!(erro instanceof Error)) return null;
  const codigo = (erro as Error & { codigo?: unknown }).codigo;
  return typeof codigo === 'string' ? codigo : null;
}

export const ehFichaAlterada = (erro: unknown): boolean => codigoDaRecusa(erro) === CODIGO_FICHA_ALTERADA;
export const ehSituacaoMudada = (erro: unknown): boolean => codigoDaRecusa(erro) === CODIGO_SITUACAO_MUDOU;

/** O corpo com a versao guardada ao abrir. Sem versao (linha antiga, sem coluna), vai sem. */
export function comVersao<T extends object>(corpo: T, versao: string | null | undefined): T & { versao?: string } {
  return typeof versao === 'string' && versao !== '' ? { ...corpo, versao } : corpo;
}

export interface Rebase<E> {
  /** O formulario novo: o seu valor onde voce mexeu, o da outra pessoa onde nao. */
  readonly estado: E;
  /** Campos que a outra pessoa mudou e que voce nao tinha tocado: agora mostram o valor dela. */
  readonly trazidos: readonly (keyof E)[];
  /** Campos que os dois mudaram: fica o seu, e a tela avisa. */
  readonly emConflito: readonly (keyof E)[];
}

/**
 * `base` e a ficha como estava quando o formulario abriu; `meu`, o que esta
 * na tela agora; `novo`, a ficha relida do servidor.
 */
export function rebasear<E extends { readonly [K in keyof E]: string }>(meu: E, base: E, novo: E): Rebase<E> {
  const estado: { [K in keyof E]: string } = { ...meu };
  const trazidos: (keyof E)[] = [];
  const emConflito: (keyof E)[] = [];
  for (const campo of Object.keys(novo) as (keyof E)[]) {
    if (novo[campo] === base[campo]) continue;
    if (meu[campo] === base[campo]) {
      estado[campo] = novo[campo];
      trazidos.push(campo);
    } else if (meu[campo] !== novo[campo]) {
      emConflito.push(campo);
    }
  }
  return { estado: estado as E, trazidos, emConflito };
}

/**
 * A frase do aviso no topo do formulario depois do rebase: o que a outra
 * pessoa mudou, com o valor de agora, e o que ficou com o seu.
 */
export function fraseDoRebase<E extends { readonly [K in keyof E]: string }>(
  rebase: Rebase<E>,
  rotulos: Readonly<Partial<Record<keyof E, string>>>,
  valorNaTela: (campo: keyof E, valor: string) => string = (_c, v) => v,
): string {
  const nome = (c: keyof E) => rotulos[c] ?? String(c);
  const valor = (c: keyof E) => {
    const v = rebase.estado[c];
    return v === '' ? 'em branco' : valorNaTela(c, v);
  };
  const partes: string[] = [];
  if (rebase.trazidos.length > 0) {
    partes.push(`O formulário foi atualizado com o que mudou: ${rebase.trazidos.map((c) => `${nome(c)} (${valor(c)})`).join(', ')}.`);
  }
  if (rebase.emConflito.length > 0) {
    partes.push(`Os dois mexeram em ${rebase.emConflito.map(nome).join(', ')}: ficou o que você digitou.`);
  }
  if (partes.length === 0) partes.push('Nenhum campo do formulário mudou.');
  partes.push('Confira e grave de novo.');
  return partes.join(' ');
}
